"use client";

import { useMemo, useSyncExternalStore } from "react";
import dynamic from "next/dynamic";
import Image from "next/image";
import Link from "next/link";
import {
  Accessibility,
  ArrowLeft,
  ArrowUpRight,
  Bus,
  Car,
  Mail,
  MapPin,
  Phone,
} from "lucide-react";

import { RichText, richTextHasContent, type RichTextValue } from "@/components/rich-text";
import { useBookingDrawer } from "@/lib/booking-context";
import type { Clinic, ServiceLink } from "@/lib/clinics";

const ClinicMap = dynamic(() => import("@/components/bloks/clinic-map"), {
  ssr: false,
  loading: () => <div className="bg-dusk-01 size-full animate-pulse" />,
});

type Stat = { number: string; label: string };
type Hours = { day: string; time: string };
type Parking = { car: string; transport: string[]; accessibility: string };

export type ClinicPageViewProps = {
  clinic: Clinic;
  heroButton: { label: string; href: string | null; target?: string };
  clinicsUrl: string;
  services: ServiceLink[];
  servicesImage: { filename: string; alt: string } | null;
  about: RichTextValue;
  stats: Stat[];
  hours: Hours[];
  parking: Parking;
};

function CheckCircle({ className }: { className?: string }) {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      aria-hidden
      className={`shrink-0 ${className ?? ""}`}
    >
      <circle cx="10" cy="10" r="9.1" stroke="currentColor" strokeWidth="1.6" />
      <path
        d="M6 10.4l2.6 2.6 5.4-5.6"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// Resolved on the client so it reflects the user's timezone without a
// hydration mismatch.
const WEEKDAYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];
const noopSubscribe = () => () => {};
function useTodayName(): string | null {
  return useSyncExternalStore(
    noopSubscribe,
    () => WEEKDAYS[(new Date().getDay() + 6) % 7],
    () => null,
  );
}

// Handles ranges ("Thu-Fri") so range rows still highlight the current day.
function dayCoversToday(dayLabel: string, todayName: string | null): boolean {
  if (!todayName) return false;
  const today = WEEKDAYS.indexOf(todayName);
  if (today === -1) return false;
  const indexOf = (s: string) => WEEKDAYS.findIndex((w) => s.trim().toLowerCase().startsWith(w));
  const parts = dayLabel.split(/[-–—]/);
  const start = indexOf(parts[0] ?? "");
  if (start === -1) return false;
  if (parts.length < 2) return start === today;
  const end = indexOf(parts[parts.length - 1] ?? "");
  if (end === -1) return start === today;
  return today >= Math.min(start, end) && today <= Math.max(start, end);
}

// Matches the global solid button (see clinics-hero).
const heroButtonClass =
  "bg-purple-03 hover:bg-purple-04 hidden h-12 w-full cursor-pointer items-center justify-center rounded-[8px] px-5 text-body font-bold text-white transition-colors md:inline-flex md:h-14 md:w-fit md:px-7";

function SectionHeading({ children }: { children: React.ReactNode }) {
  return (
    <h2 className="text-purple-05 text-[clamp(1.75rem,3vw,2.25rem)] leading-[1.12] font-bold">
      {children}
    </h2>
  );
}

export default function ClinicPageView({
  clinic,
  heroButton,
  clinicsUrl,
  services,
  servicesImage,
  about,
  stats,
  hours,
  parking,
}: ClinicPageViewProps) {
  const { setOpen } = useBookingDrawer();
  const todayName = useTodayName();
  // Open-now follows from the resolved weekday, so it stays server-safe too.
  const openNow = useMemo(() => {
    if (!todayName) return false;
    const today = hours.find((h) => dayCoversToday(h.day, todayName));
    const m = today?.time.match(/(\d{1,2}):(\d{2})\s*[—–-]\s*(\d{1,2}):(\d{2})/);
    if (!m) return false;
    const now = new Date();
    const cur = now.getHours() * 60 + now.getMinutes();
    return cur >= +m[1] * 60 + +m[2] && cur <= +m[3] * 60 + +m[4];
  }, [todayName, hours]);

  const aboutContent = richTextHasContent(about) ? about : clinic.summary;
  const hasMap = clinic.lat != null && clinic.lng != null;
  const hasTravel = Boolean(parking.car || parking.transport.length > 0 || parking.accessibility);
  const servicePhoto = servicesImage ?? clinic.photo;
  const directionsUrl = hasMap
    ? `https://www.google.com/maps/dir/?api=1&destination=${clinic.lat},${clinic.lng}`
    : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
        [clinic.name, clinic.addressLine1, clinic.addressLine2].filter(Boolean).join(", "),
      )}`;

  return (
    <>
      <section className="bg-white">
        <div className="mx-auto w-full max-w-[1280px] px-[clamp(1.25rem,4vw,2.5rem)] pt-[clamp(1.5rem,3vw,2.5rem)] pb-[clamp(2rem,4vw,3.5rem)]">
          {clinic.name && (
            <h1 className="text-purple-05 text-[clamp(2.25rem,5vw,4rem)] leading-[1.06] font-bold">
              {clinic.name}
            </h1>
          )}
          {clinic.summary && (
            <p className="mt-5 max-w-[640px] text-[clamp(1rem,1.6vw,1.2rem)] leading-[1.6] text-black/65">
              {clinic.summary}
            </p>
          )}

          <div className="flex flex-wrap items-center gap-x-6 gap-y-4 md:mt-7">
            {heroButton.href ? (
              <Link href={heroButton.href} target={heroButton.target} className={heroButtonClass}>
                {heroButton.label}
              </Link>
            ) : (
              <button type="button" onClick={() => setOpen(true, "clinic-page")} className={heroButtonClass}>
                {heroButton.label}
              </button>
            )}
          </div>

          <div className="bg-dusk-01 relative mt-9 aspect-video w-full overflow-hidden rounded-[24px] sm:aspect-16/7">
            {clinic.photo ? (
              <Image
                src={clinic.photo.filename}
                alt={clinic.photo.alt || clinic.name}
                fill
                priority
                sizes="(min-width: 1280px) 1280px, 100vw"
                className="object-cover"
              />
            ) : (
              <div className="from-dusk-02 to-purple-01 absolute inset-0 bg-linear-to-br" />
            )}
            {clinic.flagship && (
              <span className="text-purple-05 absolute top-4 right-4 rounded-full bg-white px-3 py-1 text-[12px] font-bold tracking-wide uppercase">
                Primary
              </span>
            )}
          </div>
        </div>
      </section>

      <section className="bg-white">
        <div className="mx-auto w-full max-w-[1280px] px-[clamp(1.25rem,4vw,2.5rem)] pb-[clamp(3rem,6vw,5rem)]">
          <section id="about" className="scroll-mt-28">
            <SectionHeading>About this clinic</SectionHeading>
            <RichText
              value={aboutContent}
              className="text-body mt-4 flex max-w-[680px] flex-col gap-4 leading-[1.65] text-black/70"
            />

            {stats.length > 0 && (
              <div className="mt-8 grid grid-cols-2 gap-4 lg:grid-cols-4">
                {stats.map((s, i) => (
                  <div key={i} className="bg-dusk-01 rounded-[16px] p-5">
                    <p className="text-purple-04 text-[clamp(1.5rem,2.4vw,2rem)] leading-none font-bold">
                      {s.number}
                    </p>
                    <p className="text-button mt-2 leading-snug text-black/55">{s.label}</p>
                  </div>
                ))}
              </div>
            )}
          </section>

          <section id="visiting" className="mt-16 scroll-mt-28 lg:mt-20">
            <SectionHeading>Visiting &amp; contact</SectionHeading>
            <p className="text-body mt-3 text-black/55">Everything you need to plan your visit.</p>

            <div className="mt-6 grid gap-5 md:grid-cols-[1.4fr_1fr]">
              {hasMap && (
                <div className="relative">
                  <div className="bg-dusk-01 isolate h-[320px] overflow-hidden rounded-[20px] md:h-full md:min-h-[420px]">
                    <ClinicMap
                      pins={[{ clinic, num: 1 }]}
                      activeId={clinic.uuid}
                      hoveredId={null}
                      resetKey={0}
                      onSelect={() => {}}
                      markerVariant="logo"
                    />
                  </div>
                  <a
                    href={directionsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-button text-purple-05 hover:bg-dusk-01 absolute bottom-3 left-3 z-1000 inline-flex items-center gap-1.5 rounded-full bg-white px-3.5 py-2 font-bold shadow-[0_6px_20px_-6px_rgba(39,6,88,0.28)] transition-colors"
                  >
                    Get directions
                    <ArrowUpRight className="size-4" />
                  </a>
                </div>
              )}

              <div className="flex flex-col gap-5">
                <div className="rounded-[20px] p-6 ring-1 ring-black/8">
                  <h3 className="text-h6 text-purple-05 font-bold">Contact</h3>
                  <div className="mt-4 flex flex-col gap-4">
                    {(clinic.addressLine1 || clinic.addressLine2) && (
                      <div className="flex gap-3">
                        <MapPin className="text-purple-03 mt-1 size-[18px] shrink-0" />
                        <p className="text-button leading-relaxed text-black/70">
                          {[clinic.addressLine1, clinic.addressLine2].filter(Boolean).join(", ")}
                        </p>
                      </div>
                    )}
                    {clinic.phone && (
                      <div className="flex gap-3">
                        <Phone className="text-purple-03 mt-0.5 size-[18px] shrink-0" />
                        <a
                          href={`tel:${clinic.phone.replace(/\s+/g, "")}`}
                          className="text-button hover:text-purple-03 font-semibold text-black/70 transition-colors"
                        >
                          {clinic.phone}
                        </a>
                      </div>
                    )}
                    {clinic.email && (
                      <div className="flex gap-3">
                        <Mail className="text-purple-03 mt-0.5 size-[18px] shrink-0" />
                        <a
                          href={`mailto:${clinic.email}`}
                          className="text-button hover:text-purple-03 font-semibold break-all text-black/70 transition-colors"
                        >
                          {clinic.email}
                        </a>
                      </div>
                    )}
                  </div>
                </div>

                {hours.length > 0 && (
                  <div className="rounded-[20px] p-6 ring-1 ring-black/8">
                    <div className="flex items-center justify-between gap-3">
                      <h3 className="text-h6 text-purple-05 font-bold">Opening hours</h3>
                      <span
                        className={`text-caption inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 font-bold ${
                          openNow ? "text-forest-02 bg-forest-01/12" : "bg-dusk-01 text-black/55"
                        }`}
                      >
                        {openNow && <span className="bg-forest-01 size-1.5 rounded-full" />}
                        {openNow ? "Open" : "Closed"}
                      </span>
                    </div>
                    <ul className="mt-3 flex flex-col">
                      {hours.map((h, i) => {
                        const isToday = dayCoversToday(h.day, todayName);
                        return (
                          <li
                            key={i}
                            className={`text-button flex items-center justify-between border-t border-black/8 py-2.5 first:border-t-0 ${
                              isToday ? "text-purple-05 font-bold" : "text-black/65"
                            }`}
                          >
                            <span>{h.day}</span>
                            <span>{h.time}</span>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </section>

          {services.length > 0 && (
            <section id="services" className="mt-16 scroll-mt-28 lg:mt-20">
              <div className="flex flex-col gap-8 md:min-h-[380px] md:flex-row md:items-stretch md:gap-10 lg:gap-12">
                <div className="bg-dusk-01 relative aspect-4/3 w-full overflow-hidden rounded-[24px] md:aspect-auto md:w-[44%] md:shrink-0">
                  {servicePhoto ? (
                    <Image
                      src={servicePhoto.filename}
                      alt={servicePhoto.alt || clinic.name}
                      fill
                      sizes="(min-width: 1115px) 460px, 100vw"
                      className="object-cover"
                    />
                  ) : (
                    <div className="from-dusk-02 to-purple-01 absolute inset-0 bg-linear-to-br" />
                  )}
                </div>
                <div className="flex flex-col items-center justify-center rounded-[24px] border border-black/10 bg-white p-[clamp(1.5rem,3vw,2.5rem)] text-center md:flex-1">
                  <SectionHeading>Services offered here</SectionHeading>
                  <p className="text-body mt-3 text-black/55">
                    {services.length} service
                    {services.length === 1 ? "" : "s"} available at this clinic.
                  </p>
                  <ul className="mt-6 columns-1 gap-x-8 text-left sm:columns-2">
                    {services.map((s, i) =>
                      s.href ? (
                        <li key={i} className="mb-3 break-inside-avoid">
                          <Link
                            href={s.href}
                            target={s.target}
                            className="group flex items-start gap-3"
                          >
                            <CheckCircle className="text-forest-01 mt-0.5" />
                            <span className="text-body group-hover:text-purple-04 text-black/75 transition-colors group-hover:underline">
                              {s.name}
                            </span>
                          </Link>
                        </li>
                      ) : (
                        <li key={i} className="mb-3 flex break-inside-avoid items-start gap-3">
                          <CheckCircle className="text-forest-01 mt-0.5" />
                          <span className="text-body text-black/75">{s.name}</span>
                        </li>
                      ),
                    )}
                  </ul>
                </div>
              </div>
            </section>
          )}

          {(hasTravel || hasMap) && (
            <section id="travel" className="mt-16 scroll-mt-28 lg:mt-20">
              <SectionHeading>Getting here</SectionHeading>
              <p className="text-body mt-3 max-w-[640px] text-black/55">
                How to find us, parking and accessibility.
              </p>
              <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
                {parking.car && (
                  <div className="bg-dusk-01 rounded-[16px] p-6">
                    <span className="bg-purple-03 flex size-12 items-center justify-center rounded-full text-white">
                      <Car className="size-6" />
                    </span>
                    <h3 className="text-h6 text-purple-05 mt-4 font-bold">By car</h3>
                    <p className="text-button mt-2 leading-relaxed text-black/65">{parking.car}</p>
                  </div>
                )}
                {parking.transport.length > 0 && (
                  <div className="bg-dusk-01 rounded-[16px] p-6">
                    <span className="bg-purple-03 flex size-12 items-center justify-center rounded-full text-white">
                      <Bus className="size-6" />
                    </span>
                    <h3 className="text-h6 text-purple-05 mt-4 font-bold">Public transport</h3>
                    <ul className="mt-2 flex flex-col gap-1.5">
                      {parking.transport.map((t, i) => (
                        <li key={i} className="text-button leading-relaxed text-black/65">
                          {t}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {parking.accessibility && (
                  <div className="bg-dusk-01 rounded-[16px] p-6">
                    <span className="bg-purple-03 flex size-12 items-center justify-center rounded-full text-white">
                      <Accessibility className="size-6" />
                    </span>
                    <h3 className="text-h6 text-purple-05 mt-4 font-bold">Accessibility</h3>
                    <p className="text-button mt-2 leading-relaxed text-black/65">
                      {parking.accessibility}
                    </p>
                  </div>
                )}
                {hasMap && (
                  <a
                    href={directionsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group hover:bg-purple-00 flex flex-col rounded-[16px] border border-black/10 bg-white p-6 transition-colors"
                  >
                    <span className="bg-purple-03 flex size-12 items-center justify-center rounded-full text-white">
                      <MapPin className="size-6" />
                    </span>
                    <h3 className="text-h6 text-purple-05 mt-4 font-bold">Get directions</h3>
                    <p className="text-button mt-2 leading-relaxed text-black/65">
                      Open Google Maps for turn-by-turn directions to this clinic.
                    </p>
                    <span className="text-button text-purple-03 mt-auto inline-flex items-center gap-1.5 pt-3 font-extrabold group-hover:underline">
                      Open in Maps
                      <ArrowUpRight className="size-4" />
                    </span>
                  </a>
                )}
              </div>
            </section>
          )}

          <div className="mt-12 lg:mt-16">
            <Link
              href={clinicsUrl}
              className="text-body text-purple-03 inline-flex w-fit items-center gap-[7px] font-extrabold transition-colors hover:underline"
            >
              <ArrowLeft className="size-[18px]" />
              Back to all clinics
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
